// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import { Tile, Button, TextArea } from '@carbon/react';
import { CheckmarkOutline, CloseOutline } from '@carbon/icons-react';
import { usePlanDetailContext } from './PlanDetailContext';
import PlanCharts from './PlanCharts';

function formatNumber(value: number, decimals = 0): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: decimals });
}

function formatSolveStatus(status: string): string {
  return status
    .toLowerCase()
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatWindowDate(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const PHASE_COLORS = [
  { bg: 'rgba(59, 130, 246, 0.12)', fg: '#4589ff' },
  { bg: 'rgba(239, 68, 68, 0.15)', fg: '#fa4d56' },
  { bg: 'rgba(16, 185, 129, 0.12)', fg: '#42be65' },
  { bg: 'rgba(245, 158, 11, 0.12)', fg: '#f1c21b' },
  { bg: 'rgba(139, 92, 246, 0.12)', fg: '#be95ff' },
];

function Metric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Tile className="sched-metric">
      <span className="sched-metric-label">{label}</span>
      <span className="sched-metric-value">{value}</span>
      {sub && <span className="sched-metric-sub">{sub}</span>}
    </Tile>
  );
}

function PlanOverview() {
  const { plan, planFile, onStatusUpdated } = usePlanDetailContext();
  const [submitting, setSubmitting] = useState(false);
  const [comment, setComment] = useState('');

  const handleReview = async (decision: 'APPROVED' | 'REJECTED') => {
    try {
      setSubmitting(true);
      await onStatusUpdated(decision, comment.trim() || undefined);
    } finally {
      setSubmitting(false);
    }
  };

  const { execution_time_window, phases, confidence, raw_inputs } = planFile;
  const solver = raw_inputs?.solver_info;

  const parseTime = (t: string): number => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  const durations = phases.map(p => {
    const s = parseTime(p.time_window.start);
    const e = parseTime(p.time_window.end);
    return e > s ? e - s : e + 1440 - s;
  });
  const totalDuration = durations.reduce((a, b) => a + b, 0) || 1;
  const markers =
    phases.length > 0 ? [phases[0].time_window.start, ...phases.map(p => p.time_window.end)] : [];

  return (
    <div className="sched-stack">
      <Tile className="sched-section">
        <h3 className="sched-section-title">Summary</h3>
        <p className="sched-section-text">{planFile.summary}</p>
      </Tile>

      {/* At a glance */}
      <div className="sched-metric-row">
        <Metric
          label="Total HVAC Energy"
          value={solver ? `${formatNumber(solver.objective_value)} kWh` : '—'}
          sub="Optimizer objective"
        />
        <Metric
          label="Execution Window"
          value={`${formatWindowDate(plan.date)}, ${execution_time_window.start}–${execution_time_window.end}`}
          sub={`${phases.length} phase${phases.length !== 1 ? 's' : ''}`}
        />
        <Metric label="Confidence" value={confidence} />
        <Metric
          label="Solver Status"
          value={solver ? formatSolveStatus(solver.solve_status) : '—'}
          sub={solver ? `solved in ${formatNumber(solver.solve_time_seconds, 1)}s` : undefined}
        />
      </div>

      {/* Execution window timeline */}
      {phases.length > 0 && (
        <Tile className="sched-section">
          <h3 className="sched-section-title">Execution Window</h3>
          <div className="sched-timeline-bar">
            {phases.map((phase, idx) => {
              const color = PHASE_COLORS[idx % PHASE_COLORS.length];
              return (
                <div
                  key={idx}
                  className="sched-timeline-segment"
                  style={{
                    flex: durations[idx] / totalDuration,
                    background: color.bg,
                    color: color.fg,
                  }}
                >
                  <span className="sched-timeline-segment-label">{phase.name}</span>
                </div>
              );
            })}
          </div>
          <div className="sched-timeline-markers">
            {markers.map((m, idx) => (
              <span key={idx} className="sched-timeline-marker">
                {m}
              </span>
            ))}
          </div>
        </Tile>
      )}

      {/* Equipment charts */}
      {planFile.raw_inputs && <PlanCharts rawInputs={planFile.raw_inputs} />}

      {/* Review actions */}
      {plan.status === 'PENDING' && (
        <Tile className="sched-section">
          <h3 className="sched-section-title">Review</h3>
          <TextArea
            id="review-comment"
            labelText="Comment (optional)"
            placeholder="Add a note explaining this decision…"
            value={comment}
            onChange={e => setComment(e.target.value)}
            disabled={submitting}
          />
          <div className="sched-review-actions">
            <Button
              kind="primary"
              renderIcon={CheckmarkOutline}
              disabled={submitting}
              onClick={() => handleReview('APPROVED')}
            >
              {submitting ? 'Submitting…' : 'Approve'}
            </Button>
            <Button
              kind="danger--tertiary"
              renderIcon={CloseOutline}
              disabled={submitting}
              onClick={() => handleReview('REJECTED')}
            >
              {submitting ? 'Submitting…' : 'Reject'}
            </Button>
          </div>
        </Tile>
      )}

      {/* Review decision for already-reviewed plans */}
      {plan.status !== 'PENDING' && (
        <Tile className="sched-section">
          <h3 className="sched-section-title">Review Decision</h3>
          <div className="sched-kv">
            <div className="sched-kv-row">
              <span className="sched-kv-label">Status</span>
              <span className="sched-kv-value">
                {plan.status === 'APPROVED' ? 'Approved' : 'Rejected'}
              </span>
            </div>
            {plan.review_date && (
              <div className="sched-kv-row">
                <span className="sched-kv-label">Reviewed at</span>
                <span className="sched-kv-value">
                  {new Date(plan.review_date).toLocaleString()}
                </span>
              </div>
            )}
            {plan.reviewComment && (
              <div className="sched-kv-row">
                <span className="sched-kv-label">Comment</span>
                <span className="sched-kv-value">{plan.reviewComment}</span>
              </div>
            )}
          </div>
        </Tile>
      )}
    </div>
  );
}

export default PlanOverview;
